import './claim-entity.detail.contact.scss';
import './claim-overview.scss';

import { Box, Paper, Typography } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { FormCheckBoxField } from '../../../presentation/components/form-check-box-field';
import { NewConvergeButton } from '../../../presentation/components/new-button';
import { NewFormTextField } from '../../../presentation/components/new-form-text-field';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { capitalizeLetterRemoveUnderLine } from '@converge/utils';
import {
  GetClaimInformationQuery,
  useClaimQuery,
  useUpsertClaimInformationMutation,
  useCloseClaimDetailsQuery,
} from '@converge/graphql';
import { useConvergeSnackBar } from '../../../contexts';
import {
  CircularLoader,
  ConvergeCheckbox,
  ConvergeDetailAccordion,
  NewDateField,
} from '../../../presentation';

import { formatValue, validatePastDate, validateSetErrors } from '../utils';

interface SubCoverage {
  name: string;
  uuid: string;
  selected?: boolean;
}

interface Coverage {
  name: string;
  uuid: string;
  order: number;
  type: string;
  subCoverages: SubCoverage[];
  selected?: boolean;
  sub_coverages?: SubCoverage[];
}
interface Endorsment {
  name: string;
  selected?: boolean;
  uuid: string;
  order?: number;
  type?: string;
  subCoverages?: SubCoverage[];
}

interface AdditionalCoverage {
  name: string;
  uuid: string;
}

interface RawSubCoverage {
  name: string;
  uuid?: string;
  selected: boolean;
}

interface RawCoverage {
  name: string;
  uuid?: string;
  selected: boolean;
  sub_coverages?: RawSubCoverage[];
}

interface CoverageTypeGroup {
  type: string;
  coverages: RawCoverage[];
}

interface RawEndorsement {
  name: string;
  selected: boolean;
}

interface PotentialCoverageItem {
  endorsements?: RawEndorsement[];
  coverages?: CoverageTypeGroup[];
}

interface ClaimOverviewPageProps {
  dataInformation?: GetClaimInformationQuery;
  loading: boolean;
}

export const ClaimOverviewPage = ({
  dataInformation,
  loading,
}: ClaimOverviewPageProps) => {
  const extractClaimUuidFromUrl = (
    url: string = window.location.pathname
  ): string => {
    const match = url.match(/\/claims\/([^/]+)/);
    return match ? match[1] : '';
  };

  const { notify } = useConvergeSnackBar();
  const claimUuid = extractClaimUuidFromUrl();
  const { data } = useClaimQuery({ claimUuid });
  const today = new Date();
  const [isDescription, setIsDescriptionOpen] = React.useState(true);
  const [isClaimDeclinationOpen, setIsClaimDeclinationOpen] =
    React.useState(true);
  const [isClaimDates, setIsClaimDatesOpen] = React.useState(true);
  const [isAddressOpen, setAddressOpen] = React.useState(true);
  const [isCoverageOpen, setCoverageOpen] = React.useState(true);
  const [updatedLossDecription, setUpdatedLossDecription] =
    useState<string>('');
  const [isClaimDeclined, setIsClaimDeclined] = useState<boolean>(false);
  const [isRorNA, setIsRorNA] = useState<boolean>(false);
  const [isPolNA, setIsPolNA] = useState<boolean>(false);

  const [isPeerReviewOpen, setPeerReviewOpen] = useState(true);
  const [peerReviewed, setPeerReviewed] = useState(false);
  const [referredToTMK, setReferredToTMK] = useState(false);
  const defaultValues = {
    reportedDate: '',
    lossDate: '',
    closedDate: '',
    declinationSent: '',
    rorSentDate: '',
    polSentDate: '',
  };
  // React Hook Form setup for NewDateField components
  const formMethods = useForm({
    defaultValues,
  });

  const [hasRorValue, setHasRorValue] = useState(false);
  const [hasPolValue, setHasPolValue] = useState(false);

  const description = data?.claims.edges[0].node.description;
  // State for new coverage input

  // State to track selected coverages and subcoverages
  const [selectedCoverages, setSelectedCoverages] = useState<
    Record<string, boolean>
  >({});
  const [selectedEndorsement, setSelectedEndorsement] = useState<
    Record<string, boolean>
  >({});
  const [selectedSubCoverages, setSelectedSubCoverages] = useState<
    Record<string, boolean>
  >({});

  // Auto-save state
  const [autoSaveTimeout, setAutoSaveTimeout] =
    React.useState<NodeJS.Timeout | null>(null);
  const [hasFirstSelection, setHasFirstSelection] = React.useState(false);

  const handleContactAccordion = (
    _event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setIsDescriptionOpen(newExpanded);
  };

  const { upsertClaimInformation } = useUpsertClaimInformationMutation();

  // Hook for refetching close claim details
  const { refetch: refetchCloseClaimDetails } = useCloseClaimDetailsQuery({
    claimUuid: claimUuid,
  });

  const potentialCoveragesImpacted =
    dataInformation?.claims?.edges?.[0]?.node?.potentialCoveragesImpacted;

  const additionalCoveragesImpacted =
    dataInformation?.claims?.edges?.[0]?.node?.additionalCoveragesImpacted;

  const [additionalNewCoverage, setAdditionalNewCoverage] = useState('');
  const [newCoverageName, setNewCoverageName] = useState<AdditionalCoverage[]>(
    []
  );

  // Update newCoverageName when additionalCoveragesImpacted data loads
  useEffect(() => {
    if (additionalCoveragesImpacted) {
      setNewCoverageName(additionalCoveragesImpacted);
    }
    if (description) {
      setUpdatedLossDecription(description);
    }

    // Update date fields from dataInformation
    const claimNode = dataInformation?.claims?.edges?.[0]?.node;
    if (claimNode) {
      console.log('Loading claim data:', {
        rorSentDate: claimNode.rorSentDate,
        polSentDate: claimNode.polSentDate,
      });

      // Update form values for NewDateField components - use strings in YYYY-MM-DD format
      formMethods.setValue(
        'reportedDate',
        claimNode.reportedDate ? claimNode.reportedDate.split('T')[0] : ''
      );
      formMethods.setValue(
        'lossDate',
        claimNode.lossDate ? claimNode.lossDate.split('T')[0] : ''
      );
      formMethods.setValue(
        'closedDate',
        claimNode.closedDate ? claimNode.closedDate.split('T')[0] : ''
      );
      formMethods.setValue(
        'rorSentDate',
        claimNode.rorSentDate ? claimNode.rorSentDate.split('T')[0] : ''
      );
      formMethods.setValue(
        'polSentDate',
        claimNode.polSentDate ? claimNode.polSentDate.split('T')[0] : ''
      );
      formMethods.setValue(
        'declinationSent',
        claimNode.declinationSent ? claimNode.declinationSent.split('T')[0] : ''
      );

      if (claimNode.rorSentDate) {
        setHasRorValue(true);
        setIsRorNA(false);
      } else {
        setHasRorValue(false);
        setIsRorNA(claimNode.rorSentDateNa || false);
      }

      if (claimNode.polSentDate) {
        setHasPolValue(true);
        setIsPolNA(false);
      } else {
        setHasPolValue(false);
        setIsPolNA(claimNode.polSentDateNa || false);
      }

      if (claimNode.declinationSent) {
        setIsClaimDeclined(true);
      }
    }
  }, [additionalCoveragesImpacted, description, dataInformation, formMethods]);

  useEffect(() => {
    const claimData = data?.claims.edges[0]?.node;
    if (claimData) {
      setPeerReviewed(claimData.isPeerReviewed || false);
      setReferredToTMK(claimData.isReferred || false);
    }
  }, [data]);
  const transformCoverageData = (
    jsonData: unknown
  ): {
    coverages: Coverage[];
    endorsements: Endorsment[];
    initialCoverageStates: Record<string, boolean>;
    initialSubCoverageStates: Record<string, boolean>;
    initialEndorsmentStates: Record<string, boolean>;
  } => {
    if (!jsonData || !Array.isArray(jsonData)) {
      return {
        coverages: [],
        endorsements: [],
        initialCoverageStates: {},
        initialSubCoverageStates: {},
        initialEndorsmentStates: {},
      };
    }

    const coverages: Coverage[] = [];
    const endorsements: Endorsment[] = [];
    const initialCoverageStates: Record<string, boolean> = {};
    const initialEndorsmentStates: Record<string, boolean> = {};
    const initialSubCoverageStates: Record<string, boolean> = {};
    let order = 1;

    (jsonData as PotentialCoverageItem[]).forEach(
      (item: PotentialCoverageItem) => {
        // Process endorsements if they exist in this item
        if (item.endorsements && Array.isArray(item.endorsements)) {
          item.endorsements.forEach(
            (endorsement: RawEndorsement, endorsementIndex: number) => {
              const endorsementUuid = `cov-endorsment-${endorsement.name.replace(
                /[^a-zA-Z0-9]/g,
                ''
              )}-${endorsementIndex}`;

              endorsements.push({
                name: endorsement.name || `Endorsement ${order}`,
                uuid: endorsementUuid,
                order: order++,
                type: 'endorsment',
                subCoverages: [],
              });

              initialEndorsmentStates[endorsementUuid] =
                endorsement.selected || false;
            }
          );
        }

        // Process coverages if they exist in this item
        if (item.coverages && Array.isArray(item.coverages)) {
          // item.coverages is an array of coverage type groups
          item.coverages.forEach((typeGroup: CoverageTypeGroup) => {
            const coverageType = typeGroup.type || 'liability';

            if (typeGroup.coverages && Array.isArray(typeGroup.coverages)) {
              typeGroup.coverages.forEach(
                (coverage: RawCoverage, coverageIndex: number) => {
                  const coverageUuid =
                    coverage.uuid || `cov-${coverageType}-${coverageIndex}`;

                  // Create sub-coverages if they exist
                  const subCoverages: SubCoverage[] = [];
                  if (
                    coverage.sub_coverages &&
                    Array.isArray(coverage.sub_coverages)
                  ) {
                    coverage.sub_coverages.forEach(
                      (subCov: RawSubCoverage, index: number) => {
                        // Make UUID unique by including parent coverage name to prevent collisions
                        const parentName =
                          coverage.name?.replace(/[^a-zA-Z0-9]/g, '') ||
                          `Parent${coverageIndex}`;
                        const subName =
                          subCov.name?.replace(/[^a-zA-Z0-9]/g, '') ||
                          `Sub${index}`;
                        const subCovUuid =
                          subCov.uuid ||
                          `subcov-${coverageType}-${parentName}-${subName}`;
                        subCoverages.push({
                          name: subCov.name || `Sub Coverage ${index + 1}`,
                          uuid: subCovUuid,
                        });
                        // Set initial sub-coverage state from data
                        initialSubCoverageStates[subCovUuid] =
                          subCov.selected || false;
                      }
                    );
                  }

                  coverages.push({
                    name: coverage.name || `Coverage ${order}`,
                    uuid: coverageUuid,
                    order: order++,
                    type: coverageType,
                    subCoverages,
                  });

                  initialCoverageStates[coverageUuid] =
                    coverage.selected || false;
                }
              );
            }
          });
        }
      }
    );

    return {
      coverages,
      initialCoverageStates,
      initialSubCoverageStates,
      endorsements,
      initialEndorsmentStates,
    };
  };

  const transformedData = React.useMemo(() => {
    return transformCoverageData(potentialCoveragesImpacted);
  }, [potentialCoveragesImpacted]);

  const coverageData =
    transformedData.coverages.length > 0 ? transformedData.coverages : [];

  const endormsnetData =
    transformedData.endorsements.length > 0 ? transformedData.endorsements : [];
  const createUpdatedPotentialCoveragesImpacted = () => {
    if (
      !potentialCoveragesImpacted ||
      !Array.isArray(potentialCoveragesImpacted)
    ) {
      return potentialCoveragesImpacted;
    }

    // Clone the original data
    const updatedData = JSON.parse(JSON.stringify(potentialCoveragesImpacted));

    // Update each item
    updatedData.forEach((item: PotentialCoverageItem) => {
      if (item.coverages && Array.isArray(item.coverages)) {
        // item.coverages is an array of coverage type groups
        item.coverages.forEach((typeGroup: CoverageTypeGroup) => {
          const coverageType = typeGroup.type || 'liability';

          if (typeGroup.coverages && Array.isArray(typeGroup.coverages)) {
            typeGroup.coverages.forEach(
              (coverage: RawCoverage, coverageIndex: number) => {
                // Generate the same UUID we used for the frontend
                const coverageUuid =
                  coverage.uuid || `cov-${coverageType}-${coverageIndex}`;

                // Update coverage selected state if it changed
                const isSelectedInFrontend = selectedCoverages[coverageUuid];
                if (isSelectedInFrontend !== undefined) {
                  coverage.selected = isSelectedInFrontend;
                }

                // Update sub-coverage selected states
                if (
                  coverage.sub_coverages &&
                  Array.isArray(coverage.sub_coverages)
                ) {
                  coverage.sub_coverages.forEach(
                    (subCov: RawSubCoverage, index: number) => {
                      // Generate the same UUID we used for the frontend (must match exactly)
                      const parentName =
                        coverage.name?.replace(/[^a-zA-Z0-9]/g, '') ||
                        `Parent${coverageIndex}`;
                      const subName =
                        subCov.name?.replace(/[^a-zA-Z0-9]/g, '') ||
                        `Sub${index}`;
                      const subCovUuid =
                        subCov.uuid ||
                        `subcov-${coverageType}-${parentName}-${subName}`;

                      // Update sub-coverage selected state if it changed
                      const isSubSelectedInFrontend =
                        selectedSubCoverages[subCovUuid];
                      if (isSubSelectedInFrontend !== undefined) {
                        subCov.selected = isSubSelectedInFrontend;
                      }
                    }
                  );
                }
              }
            );
          }
        });
      }
    });
    return updatedData;
  };

  const autoSaveChangesforEndorement = useCallback(
    async (currentEndorsment: Record<string, boolean>) => {
      try {
        if (
          !potentialCoveragesImpacted ||
          !Array.isArray(potentialCoveragesImpacted)
        ) {
          return;
        }

        // Clone the original data
        const updatedData = JSON.parse(
          JSON.stringify(potentialCoveragesImpacted)
        );

        // Update each item with the current state values
        updatedData.forEach((item: PotentialCoverageItem) => {
          if (item.endorsements && Array.isArray(item.endorsements)) {
            item.endorsements.forEach(
              (endorsement: RawEndorsement, endorsementIndex: number) => {
                // Generate the same UUID we used for the frontend
                const endorsementUuid = `cov-endorsment-${endorsement.name.replace(
                  /[^a-zA-Z0-9]/g,
                  ''
                )}-${endorsementIndex}`;

                // Update endorsement selected state if it changed
                const isSelectedInFrontend = currentEndorsment[endorsementUuid];
                if (isSelectedInFrontend !== undefined) {
                  endorsement.selected = isSelectedInFrontend;
                }
              }
            );
          }
        });

        await upsertClaimInformation({
          claimInput: {
            claimUuid: claimUuid,
            potentialCoveragesImpacted: updatedData,
          },
        });

        // Refetch close claim details after successful upsert
        await refetchCloseClaimDetails();

        notify(`Auto-save completed successfully`, {
          variant: 'success',
        });
      } catch (error) {
        notify(`Auto-save failed`, {
          variant: 'error',
        });
      }
    },
    [
      claimUuid,
      notify,
      potentialCoveragesImpacted,
      upsertClaimInformation,
      refetchCloseClaimDetails,
    ]
  );

  // Auto-save function that accepts current state values
  const autoSaveChanges = useCallback(
    async (
      currentCoverages: Record<string, boolean>,
      currentSubCoverages: Record<string, boolean>
    ) => {
      try {
        if (
          !potentialCoveragesImpacted ||
          !Array.isArray(potentialCoveragesImpacted)
        ) {
          return;
        }

        // Clone the original data
        const updatedData = JSON.parse(
          JSON.stringify(potentialCoveragesImpacted)
        );

        // Update each item with the current state values
        updatedData.forEach((item: PotentialCoverageItem) => {
          if (item.coverages && Array.isArray(item.coverages)) {
            // item.coverages is an array of coverage type groups
            item.coverages.forEach((typeGroup: CoverageTypeGroup) => {
              const coverageType = typeGroup.type || 'liability';

              if (typeGroup.coverages && Array.isArray(typeGroup.coverages)) {
                typeGroup.coverages.forEach(
                  (coverage: RawCoverage, coverageIndex: number) => {
                    // Generate the same UUID we used for the frontend
                    const coverageUuid =
                      coverage.uuid || `cov-${coverageType}-${coverageIndex}`;

                    // Update coverage selected state if it changed
                    const isSelectedInFrontend = currentCoverages[coverageUuid];
                    if (isSelectedInFrontend !== undefined) {
                      coverage.selected = isSelectedInFrontend;
                    }

                    // Update sub-coverage selected states
                    if (
                      coverage.sub_coverages &&
                      Array.isArray(coverage.sub_coverages)
                    ) {
                      coverage.sub_coverages.forEach(
                        (subCov: RawSubCoverage, index: number) => {
                          // Generate the same UUID we used for the frontend (must match exactly)
                          const parentName =
                            coverage.name?.replace(/[^a-zA-Z0-9]/g, '') ||
                            `Parent${coverageIndex}`;
                          const subName =
                            subCov.name?.replace(/[^a-zA-Z0-9]/g, '') ||
                            `Sub${index}`;
                          const subCovUuid =
                            subCov.uuid ||
                            `subcov-${coverageType}-${parentName}-${subName}`;

                          // Update sub-coverage selected state if it changed
                          const isSubSelectedInFrontend =
                            currentSubCoverages[subCovUuid];
                          if (isSubSelectedInFrontend !== undefined) {
                            subCov.selected = isSubSelectedInFrontend;
                          }
                        }
                      );
                    }
                  }
                );
              }
            });
          }
        });

        await upsertClaimInformation({
          claimInput: {
            claimUuid: claimUuid,
            potentialCoveragesImpacted: updatedData,
          },
        });

        // Refetch close claim details after successful upsert
        await refetchCloseClaimDetails();

        notify(`Potenital coverages impacted has been updated`, {
          variant: 'success',
        });
      } catch (error) {
        notify(`Potenital coverages impacted has been failed`, {
          variant: 'error',
        });
      }
    },
    [
      claimUuid,
      notify,
      potentialCoveragesImpacted,
      upsertClaimInformation,
      refetchCloseClaimDetails,
    ]
  );

  // Additinal coverage
  const addingNewCoverageHandler = useCallback(
    async (
      updatedAdditionalCoveragesImpacted: AdditionalCoverage[],
      message: string
    ) => {
      try {
        const result = await upsertClaimInformation({
          claimInput: {
            claimUuid: claimUuid,
            additionalCoveragesImpacted: updatedAdditionalCoveragesImpacted,
          },
        });

        // Refetch close claim details after successful upsert
        await refetchCloseClaimDetails();

        // Update local state with the returned additionalCoveragesImpacted data
        if (result.data?.upsertClaim.additionalCoveragesImpacted) {
          setNewCoverageName(
            result.data?.upsertClaim.additionalCoveragesImpacted
          );
        }

        notify(`Additional coverage has been ${message}`, {
          variant: 'success',
        });
      } catch (error) {
        notify(
          `Failed to ${
            message === 'added' ? 'add' : 'delete'
          } additional coverage`,
          {
            variant: 'error',
          }
        );
      }
    },
    [claimUuid, notify, upsertClaimInformation, refetchCloseClaimDetails]
  );

  const lossDecriptionHandler = useCallback(
    async (updateLossDescription: string) => {
      try {
        const { data } = await upsertClaimInformation({
          claimInput: {
            claimUuid: claimUuid,
            description:
              updateLossDescription?.trim()?.length > 0
                ? updateLossDescription
                : null,
          },
        });

        // Refetch close claim details after successful upsert
        await refetchCloseClaimDetails();

        setUpdatedLossDecription(
          data?.upsertClaim?.description || updateLossDescription
        );
        notify(`Loss description has been updated`, {
          variant: 'success',
        });
      } catch (error) {
        notify(`Failed to update loss description`, {
          variant: 'error',
        });
      }
    },
    [claimUuid, notify, upsertClaimInformation, refetchCloseClaimDetails]
  );

  const dateHandler = useCallback(
    async (fieldName: string, date: string | null, isNA?: boolean) => {
      try {
        // Create the claim input object with the dynamic field name
        const claimInput: {
          claimUuid: string;
          reportedDate?: string | null;
          lossDate?: string | null;
          closedDate?: string | null;
          rorSentDate?: string | null;
          rorSentDateNa?: boolean | null;
          polSentDate?: string | null;
          polSentDateNa?: boolean | null;
        } = {
          claimUuid: claimUuid,
        };

        // Set the appropriate field based on fieldName
        if (fieldName === 'lossDate') {
          claimInput.lossDate = date;
        } else if (fieldName === 'reportedDate') {
          claimInput.reportedDate = date;
        } else if (fieldName === 'closedDate') {
          claimInput.closedDate = date;
        } else if (fieldName === 'rorSentDate') {
          claimInput.rorSentDate = date;
          claimInput.rorSentDateNa = isNA !== undefined ? isNA : null;
        } else if (fieldName === 'polSentDate') {
          claimInput.polSentDate = date;
          claimInput.polSentDateNa = isNA !== undefined ? isNA : null;
        }

        await upsertClaimInformation({
          claimInput,
        });

        await refetchCloseClaimDetails();

        formMethods.setValue(
          fieldName as
            | 'reportedDate'
            | 'lossDate'
            | 'closedDate'
            | 'rorSentDate',
          date ? date.split('T')[0] : ''
        );

        const fieldDisplayNames = {
          reportedDate: 'Reported Date',
          lossDate: 'Date of Loss',
          closedDate: 'Closed Date',
          rorSentDate: 'ROR Sent',
          polSentDate: 'POL Sent',
        };

        notify(
          `${
            fieldDisplayNames[fieldName as keyof typeof fieldDisplayNames]
          } has been updated`,
          {
            variant: 'success',
          }
        );
      } catch (error) {
        const fieldDisplayNames = {
          reportedDate: 'Reported Date',
          lossDate: 'Loss Date',
          closedDate: 'Closed Date',
          rorSentDate: 'ROR Sent',
          polSentDate: 'POL Sent',
        };

        notify(
          `Failed to update ${
            fieldDisplayNames[fieldName as keyof typeof fieldDisplayNames]
          }`,
          {
            variant: 'error',
          }
        );
      }
    },
    [
      claimUuid,
      notify,
      upsertClaimInformation,
      refetchCloseClaimDetails,
      formMethods,
    ]
  );

  const declinationSentHandler = useCallback(
    async (date: string | null) => {
      try {
        // Create the claim input object with the dynamic field name
        const claimInput: {
          claimUuid: string;
          reportedDate?: string | null;
          lossDate?: string | null;
          closedDate?: string | null;
          declinationSent?: string | null;
        } = {
          claimUuid: claimUuid,
          declinationSent: date,
        };

        await upsertClaimInformation({
          claimInput,
        });

        await refetchCloseClaimDetails();

        notify(
          `
            Declination Sent field has been updated`,
          {
            variant: 'success',
          }
        );
      } catch (error) {
        notify(`Failed to update Declination Sent date`, {
          variant: 'error',
        });
      }
    },
    [claimUuid, notify, upsertClaimInformation, refetchCloseClaimDetails]
  );

  // Trigger auto-save with 10-second delay after first selection
  const triggerAutoSave = React.useCallback(
    (
      currentCoverages: Record<string, boolean>,
      currentSubCoverages: Record<string, boolean>
    ) => {
      // Clear existing timeout if any
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }

      // Set new timeout for 10 seconds
      const newTimeout = setTimeout(() => {
        autoSaveChanges(currentCoverages, currentSubCoverages);
      }, 2000); // 5 seconds

      setAutoSaveTimeout(newTimeout);
    },
    [autoSaveTimeout, autoSaveChanges]
  );

  const triggerAutoSaveforEndrosment = React.useCallback(
    (currentEndorsment: Record<string, boolean>) => {
      // Clear existing timeout if any
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }

      // Set new timeout for 10 seconds
      const newTimeout = setTimeout(() => {
        autoSaveChangesforEndorement(currentEndorsment);
      }, 2000); // 5 seconds

      setAutoSaveTimeout(newTimeout);
    },
    [autoSaveTimeout, autoSaveChangesforEndorement]
  );

  createUpdatedPotentialCoveragesImpacted();
  useEffect(() => {
    if (transformedData.coverages.length > 0) {
      setSelectedCoverages(transformedData.initialCoverageStates);
      setSelectedEndorsement(transformedData.initialEndorsmentStates);
      setSelectedSubCoverages(transformedData.initialSubCoverageStates);
    }
  }, [
    potentialCoveragesImpacted,
    transformedData.coverages.length,
    transformedData.initialCoverageStates,
    transformedData.initialSubCoverageStates,
    transformedData.initialEndorsmentStates,
  ]); // Only depend on original data to prevent infinite loops

  const handleAddressAccordion = (
    _event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setAddressOpen(newExpanded);
  };
  const handleDateAccordion = (
    _event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setIsClaimDatesOpen(newExpanded);
  };

  const handleCoverageAccordion = (
    _event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setCoverageOpen(newExpanded);
  };

  const handleDeclinationAccordion = (
    _event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setIsClaimDeclinationOpen(newExpanded);
  };

  const handlePeerReviewAccordion = (
    _event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setPeerReviewOpen(newExpanded);
  };

  const handlePeerReviewedChange = async (checked: boolean) => {
    setPeerReviewed(checked);
    try {
      await upsertClaimInformation({
        claimInput: {
          claimUuid: claimUuid,
          isPeerReviewed: checked,
        },
      });
      notify('Peer reviewed has been updated', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error updating peer reviewed status:', error);
      notify('Failed to update peer reviewed status', { variant: 'error' });
      setPeerReviewed(!checked);
    }
  };

  const handleReferredToTMKChange = async (checked: boolean) => {
    setReferredToTMK(checked);
    try {
      await upsertClaimInformation({
        claimInput: {
          claimUuid: claimUuid,
          isReferred: checked,
        },
      });
      notify('Referred to TMK status updated successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error updating referred to TMK status:', error);
      notify('Failed to update referred to TMK status', { variant: 'error' });
      setReferredToTMK(!checked);
    }
  };

  const handleEndorsmentChange =
    (endorsement: Endorsment) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      const endorsementUuid = endorsement.uuid;

      if (!hasFirstSelection) {
        setHasFirstSelection(true);
      }

      const newEndoresment = {
        ...selectedEndorsement,
        [endorsementUuid]: isChecked,
      };

      // Update local state
      setSelectedEndorsement(newEndoresment);

      // Trigger auto-save with the latest state values
      if (hasFirstSelection || !hasFirstSelection) {
        triggerAutoSaveforEndrosment(newEndoresment);
      }
    };

  const handleCoverageChange =
    (coverage: Coverage) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      const coverageUuid = coverage.uuid;

      if (!hasFirstSelection) {
        setHasFirstSelection(true);
      }

      const newCoverages = {
        ...selectedCoverages,
        [coverageUuid]: isChecked,
      };

      const newSubCoverages = { ...selectedSubCoverages };
      if (!isChecked) {
        coverage.subCoverages.forEach((subcoverage) => {
          if (newSubCoverages[subcoverage.uuid]) {
            newSubCoverages[subcoverage.uuid] = false;
          }
        });
      }

      // Update local state
      setSelectedCoverages(newCoverages);
      setSelectedSubCoverages(newSubCoverages);

      // Trigger auto-save with the latest state values
      if (hasFirstSelection || !hasFirstSelection) {
        triggerAutoSave(newCoverages, newSubCoverages);
      }
    };

  // Handle subcoverage checkbox change
  const handleSubCoverageChange =
    (subcoverageUuid: string, parentCoverageUuid: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;

      if (isChecked && !selectedCoverages[parentCoverageUuid]) {
        event.preventDefault();
        return;
      }

      // Mark first selection and trigger auto-save
      if (!hasFirstSelection) {
        setHasFirstSelection(true);
      }

      // Calculate the new subcoverage state
      const newSubCoverages = {
        ...selectedSubCoverages,
        [subcoverageUuid]: isChecked,
      };

      setSelectedSubCoverages(newSubCoverages);

      // Trigger auto-save with the latest state values
      if (hasFirstSelection || !hasFirstSelection) {
        triggerAutoSave(selectedCoverages, newSubCoverages);
      }
    };

  const [dateFieldErrors, setDateFieldErrors] = useState<{
    reportedDate: boolean;
    lossDate: boolean;
    closedDate: boolean;
    declinationSent: boolean;
    rorSentDate: boolean;
    polSentDate: boolean;
    lossDateMessage?: string;
    rorSentDateMessage?: string;
    polSentDateMessage?: string;
    declinationSentMessage?: string;
  }>({
    reportedDate: false,
    lossDate: false,
    closedDate: false,
    declinationSent: false,
    rorSentDate: false,
    polSentDate: false,
  });

  return (
    <Box className="claim-overview">
      <Paper elevation={0}>
        <ConvergeDetailAccordion
          expanded={isClaimDates}
          onChange={handleDateAccordion}
          title="CLAIM DATES"
          description="This information has been generated by the original First Notice of Loss. You can modify these dates as necessary."
          testId="claim-dates-accordion"
        >
          <FormProvider {...formMethods}>
            <Box width={'80%'} paddingLeft={'30px'}>
              <Box display="flex" gap={4} flexWrap="wrap" width="100%">
                <Box flex={1} minWidth="100px">
                  <NewDateField
                    name="reportedDate"
                    disabled={true}
                    maxDate={today}
                    helperText={
                      dateFieldErrors.reportedDate ? 'Invalid date format' : ''
                    }
                    sx={{
                      '.MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: '1px #666 solid',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#23a892',
                        },
                        '&.Mui-disabled': {
                          '& .MuiInputBase-input': {
                            height: '15px',
                            transform: 'translateY(3px)',
                          },
                        },
                        minHeight: '39px',
                        maxHeight: '39px',
                      },

                      '.MuiButtonBase-root.MuiIconButton-root.MuiIconButton-edgeEnd.MuiIconButton-sizeMedium':
                        {
                          marginRight: '3px',
                        },

                      '& .MuiFormHelperText-root': {
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        margin: 0,
                        padding: '2px 4px',
                        fontSize: '11px',
                        lineHeight: '1.2',
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                      position: 'relative',
                    }}
                    label="DATE CLAIM REPORTED"
                    onDateChange={(date) => {
                      const isInvalid = formatValue(date) === 'Invalid date';
                      setDateFieldErrors((prev) => ({
                        ...prev,
                        reportedDate: isInvalid,
                      }));

                      if (!isInvalid) {
                        dateHandler(
                          'reportedDate',
                          date ? date.toISOString() : null
                        );
                      }
                    }}
                    data-testid="claim-reopened-claim"
                  />
                </Box>
                <Box flex={1} minWidth="100px">
                  <NewDateField
                    name="lossDate"
                    label="DATE OF LOSS"
                    maxDate={today}
                    disableValidation={true}
                    onDateChange={(date, inputValue) => {
                      const dateToValidate = date ? new Date(date) : null;
                      const { isInvalid, errorMessage } = validatePastDate(
                        dateToValidate,
                        inputValue
                      );

                      if (isInvalid) {
                        formMethods.setError('lossDate', {
                          type: 'validation',
                          message: errorMessage,
                        });
                      } else {
                        formMethods.clearErrors('lossDate');
                      }

                      setDateFieldErrors((prev) => ({
                        ...prev,
                        lossDate: isInvalid,
                        lossDateMessage: errorMessage,
                      }));

                      if (!isInvalid && date) {
                        dateHandler(
                          'lossDate',
                          date ? date.toISOString() : null
                        );
                      }
                    }}
                    sx={{
                      '.MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: '1px #666 solid',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#23a892',
                        },
                        '&.Mui-disabled': {
                          '& .MuiInputBase-input': {
                            height: '20px',
                          },
                        },
                      },
                      '.MuiButtonBase-root.MuiIconButton-root.MuiIconButton-edgeEnd.MuiIconButton-sizeMedium':
                        {
                          marginRight: '3px',
                        },

                      '& .MuiFormHelperText-root': {
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        margin: 0,
                        padding: '2px 4px',
                        fontSize: '11px',
                        lineHeight: '1.2',
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: '600 !important',
                      },
                      position: 'relative',
                    }}
                    data-testid="claim-date-of-loss"
                  />
                </Box>
                <Box flex={1} minWidth="100px">
                  <NewDateField
                    name="closedDate"
                    label="DATE CLAIM CLOSED"
                    disabled={true}
                    maxDate={today}
                    onDateChange={(date) => {
                      const isInvalid = formatValue(date) === 'Invalid date';
                      setDateFieldErrors((prev) => ({
                        ...prev,
                        closedDate: isInvalid,
                      }));

                      if (!isInvalid) {
                        dateHandler(
                          'closedDate',
                          date ? date.toISOString() : null
                        );
                      }
                    }}
                    helperText={
                      dateFieldErrors.closedDate ? 'Invalid date format' : ''
                    }
                    sx={{
                      '.MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: '1px #666 solid',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#23a892',
                        },
                        '&.Mui-disabled': {
                          '& .MuiInputBase-input': {
                            height: '15px',
                            transform: 'translateY(3px)',
                          },
                        },
                        minHeight: '39px',
                        maxHeight: '39px',
                      },
                      '.MuiButtonBase-root.MuiIconButton-root.MuiIconButton-edgeEnd.MuiIconButton-sizeMedium':
                        {
                          marginRight: '3px',
                        },

                      '& .MuiFormHelperText-root': {
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        margin: 0,
                        padding: '2px 4px',
                        fontSize: '11px',
                        lineHeight: '1.2',
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                      position: 'relative',
                    }}
                    data-testid="claim-closed-date"
                  />
                </Box>
              </Box>
            </Box>

            <Box
              width={'55%'}
              sx={{ display: 'flex', alignItems: 'center', mt: 2 }}
            >
              <Box
                width={'80%'}
                style={{ marginLeft: '30px', marginTop: '20px' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    mb: 2,
                  }}
                >
                  <Box>
                    <NewDateField
                      disabled={isRorNA}
                      name="rorSentDate"
                      maxDate={today}
                      disableValidation={true}
                      sx={{
                        '.MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: '1px #666 solid',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#23a892',
                          },
                          '&.Mui-disabled': {
                            '& .MuiInputBase-input': {
                              height: '15px',
                              transform: 'translateY(3px)',
                            },
                          },
                          minHeight: '39px',
                          maxHeight: '39px',
                        },
                        '.MuiButtonBase-root.MuiIconButton-root.MuiIconButton-edgeEnd.MuiIconButton-sizeMedium':
                          {
                            marginRight: '3px',
                          },

                        '& .MuiFormHelperText-root': {
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          margin: 0,
                          padding: '2px 4px',
                          fontSize: '11px',
                          lineHeight: '1.2',
                          zIndex: 10,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: '600 !important',
                        },
                        position: 'relative',
                      }}
                      label="ROR SENT"
                      onDateChange={(date, inputValue) => {
                        const { isInvalid, errorMessage } = validateSetErrors(
                          'rorSentDate',
                          date,
                          inputValue,
                          formMethods
                        );

                        setDateFieldErrors((prev) => ({
                          ...prev,
                          rorSentDate: isInvalid,
                          rorSentDateMessage: errorMessage,
                        }));

                        if (date) {
                          setHasRorValue(true);
                          setIsRorNA(false);
                        } else {
                          setHasRorValue(false);
                        }
                        if (!isInvalid && date) {
                          dateHandler(
                            'rorSentDate',
                            date ? date.toISOString() : null,
                            false
                          );
                        }
                      }}
                      data-testid="claim-Declinatio-Sent"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 3,
                    }}
                  >
                    <ConvergeCheckbox
                      name={`rorSentDateNa`}
                      checked={isRorNA}
                      onChange={(checked) => {
                        setIsRorNA(checked);
                        if (checked === true) {
                          dateHandler('rorSentDate', null, true);
                        } else {
                          dateHandler('rorSentDate', null, false);
                        }
                      }}
                      dataTestId={'rorSentDateN/A'}
                      disabled={hasRorValue}
                      className={
                        hasRorValue ? 'disabled-inactive-overview' : ''
                      }
                    />
                    <Typography
                      className="checkbox-text-overview"
                      style={{
                        fontSize: '14px',
                        opacity: hasRorValue ? 0.5 : 1,
                      }}
                    >
                      N/A
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                width={'80%'}
                style={{ marginLeft: '30px', marginTop: '20px' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    mb: 2,
                  }}
                >
                  <Box>
                    <NewDateField
                      disabled={isPolNA}
                      name="polSentDate"
                      maxDate={today}
                      disableValidation={true}
                      sx={{
                        '.MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: '1px #666 solid',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#23a892',
                          },
                          '&.Mui-disabled': {
                            '& .MuiInputBase-input': {
                              height: '15px',
                              transform: 'translateY(3px)',
                            },
                          },
                          minHeight: '39px',
                          maxHeight: '39px',
                        },
                        '.MuiButtonBase-root.MuiIconButton-root.MuiIconButton-edgeEnd.MuiIconButton-sizeMedium':
                          {
                            marginRight: '3px',
                          },

                        '& .MuiFormHelperText-root': {
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          margin: 0,
                          padding: '2px 4px',
                          fontSize: '11px',
                          lineHeight: '1.2',
                          zIndex: 10,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: '600 !important',
                        },
                        position: 'relative',
                      }}
                      label="POL SENT"
                      onDateChange={(date, inputValue) => {
                        const { isInvalid, errorMessage } = validateSetErrors(
                          'polSentDate',
                          date,
                          inputValue,
                          formMethods
                        );

                        setDateFieldErrors((prev) => ({
                          ...prev,
                          polSentDate: isInvalid,
                          polSentDateMessage: errorMessage,
                        }));

                        if (date) {
                          setHasPolValue(true);
                          setIsPolNA(false);
                        } else {
                          setHasPolValue(false);
                        }
                        if (!isInvalid && date) {
                          dateHandler(
                            'polSentDate',
                            date ? date.toISOString() : null,
                            false
                          );
                        }
                      }}
                      data-testid="claim-Declinatio-Sent"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 3,
                    }}
                  >
                    <ConvergeCheckbox
                      name={`polSentDateNa`}
                      checked={isPolNA}
                      onChange={(checked) => {
                        if (!hasPolValue) {
                          setIsPolNA(checked);
                          if (checked === true) {
                            dateHandler('polSentDate', null, true);
                          } else {
                            dateHandler('polSentDate', null, false);
                          }
                        }
                      }}
                      dataTestId={'polSentDateNAcheckbox'}
                      disabled={hasPolValue}
                      className={
                        hasPolValue ? 'disabled-inactive-overview' : ''
                      }
                    />
                    <Typography
                      className="checkbox-text-overview"
                      style={{
                        fontSize: '14px',
                        opacity: hasPolValue ? 0.5 : 1,
                      }}
                    >
                      N/A
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </FormProvider>
        </ConvergeDetailAccordion>

        {/* PEER REVIEW AND REFERRAL Section */}
        <ConvergeDetailAccordion
          expanded={isPeerReviewOpen}
          onChange={handlePeerReviewAccordion}
          title="PEER REVIEW AND REFERRAL"
          description="Indicate if any of this information is necessary for the claim."
          detailsClassName="accordion-details-style"
          testId="peer-review-accordion"
        >
          <Box width={'70%'} style={{ marginLeft: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ConvergeCheckbox
                  checked={peerReviewed}
                  onChange={handlePeerReviewedChange}
                  dataTestId="peer-reviewed-checkbox"
                />
                <Typography
                  className="checkbox-text-overview"
                  style={{ fontSize: '14px' }}
                >
                  Peer Reviewed (QA)
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ConvergeCheckbox
                  checked={referredToTMK}
                  onChange={handleReferredToTMKChange}
                  dataTestId="referred-to-tmk-checkbox"
                />
                <Typography
                  className="checkbox-text-overview"
                  style={{ fontSize: '14px' }}
                >
                  Referred to TMK
                </Typography>
              </Box>
            </Box>
          </Box>
        </ConvergeDetailAccordion>

        <ConvergeDetailAccordion
          expanded={isClaimDeclinationOpen}
          onChange={handleDeclinationAccordion}
          title="CLAIM DECLINATION"
          description="If this claim is denied, please indicate it here and enter the date of the declination letter."
          detailsClassName="accordion-details-style"
          testId="claim-declination-accordion"
        >
          <FormProvider {...formMethods}>
            <Box width={'70%'} style={{ marginLeft: '20px' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 8, mb: 2 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 3,
                  }}
                >
                  <ConvergeCheckbox
                    name={`claim`}
                    onChange={(checked) => {
                      setIsClaimDeclined(checked);
                      if (checked) {
                        // When checked, set today's date in local timezone
                        const today = new Date();
                        const year = today.getFullYear();
                        const month = String(today.getMonth() + 1).padStart(
                          2,
                          '0'
                        );
                        const day = String(today.getDate()).padStart(2, '0');
                        const todayFormatted = `${year}-${month}-${day}`;
                        const todayISO = `${todayFormatted}T00:00:00.000Z`;

                        formMethods.setValue(
                          'declinationSent',
                          todayFormatted,
                          {
                            shouldValidate: false,
                          }
                        );
                        declinationSentHandler(todayISO);
                      } else {
                        // When unchecked, clear the date
                        formMethods.setValue('declinationSent', '', {
                          shouldValidate: false,
                        });
                        formMethods.resetField('declinationSent', {
                          defaultValue: '',
                        });
                        setDateFieldErrors((prev) => ({
                          ...prev,
                          declinationSent: false,
                        }));
                        declinationSentHandler(null);
                      }
                    }}
                    checked={isClaimDeclined}
                    dataTestId={'CliamDeclined'}
                  />
                  <Typography
                    className="checkbox-text-overview"
                    style={{
                      fontSize: '14px',
                    }}
                  >
                    Claim Declined
                  </Typography>
                </Box>
                <Box>
                  <NewDateField
                    key={isClaimDeclined ? 'enabled' : 'disabled'}
                    disabled={!isClaimDeclined}
                    name="declinationSent"
                    maxDate={today}
                    disableValidation={true}
                    sx={{
                      '.MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: '1px #666 solid',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#23a892',
                        },
                        '&.Mui-disabled': {
                          '& .MuiInputBase-input': {
                            height: '15px',
                            transform: 'translateY(3px)',
                          },
                        },
                        minHeight: '39px',
                        maxHeight: '39px',
                      },
                      '.MuiButtonBase-root.MuiIconButton-root.MuiIconButton-edgeEnd.MuiIconButton-sizeMedium':
                        {
                          marginRight: '3px',
                        },

                      '& .MuiFormHelperText-root': {
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        margin: 0,
                        padding: '2px 4px',
                        fontSize: '11px',
                        lineHeight: '1.2',
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: '600 !important',
                      },
                      position: 'relative',
                    }}
                    label="DECLINATION SENT"
                    onDateChange={(date, inputValue) => {
                      if (isClaimDeclined) {
                        const { isInvalid, errorMessage } = validateSetErrors(
                          'declinationSent',
                          date,
                          inputValue,
                          formMethods
                        );

                        setDateFieldErrors((prev) => ({
                          ...prev,
                          declinationSent: isInvalid,
                          declinationSentMessage: errorMessage,
                        }));

                        if (!isInvalid && date) {
                          declinationSentHandler(
                            date ? date.toISOString() : null
                          );
                        }
                      }
                    }}
                    data-testid="claim-Declinatio-Sent"
                  />
                </Box>
              </Box>
            </Box>
          </FormProvider>
        </ConvergeDetailAccordion>
        <ConvergeDetailAccordion
          expanded={isDescription}
          onChange={handleContactAccordion}
          title="LOSS DESCRIPTION"
          description="This information has been generated by the original First Notice of Loss. You can modify this as necessary."
          detailsClassName="accordion-details-style"
          testId="loss-description-accordion"
        >
          <Box width={'91%'} style={{ marginLeft: '20px', height: '140px' }}>
            <NewFormTextField
              InputProps={{
                readOnly: false,
              }}
              // disableHoverBorder={isNotActive ? true : false}
              label=""
              name={'lossDescription'}
              multiline={true}
              value={updatedLossDecription}
              onBlur={(e) => {
                if (e?.target?.value?.trim() !== description) {
                  lossDecriptionHandler(e?.target?.value?.trim());
                }
              }}
            />
          </Box>
          {/* <Box className="loss-description-text-container">{description}</Box> */}
        </ConvergeDetailAccordion>
        <ConvergeDetailAccordion
          expanded={isCoverageOpen}
          onChange={handleCoverageAccordion}
          title="POTENTIAL COVERAGES IMPACTED"
          description="These are the coverages currently included in the policy. This will not affect any reserves or payments needed for this claim."
          paddingBottom="25px"
          testId="potential-coverages-accordion"
        >
          {!loading ? (
            <Box className="coverage-container">
              <Box sx={{ width: '25%' }}>
                <Box
                  className="coverage-title"
                  sx={{
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    marginBottom: '36px !important',
                  }}
                >
                  Claims-Made Liability Coverage
                </Box>
                {coverageData
                  .filter((coverage) => coverage.type === 'liability')
                  .map((coverage) => (
                    <Box
                      key={coverage.uuid}
                      sx={{
                        mb: coverage.subCoverages.length > 0 ? 0.7 : 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: coverage.subCoverages.length > 0 ? 0.2 : 1,
                          '& .MuiFormControlLabel-root': {
                            alignItems: 'flex-start',
                          },
                          '& .MuiButtonBase-root': {
                            paddingTop: '1px',
                          },
                        }}
                      >
                        <FormCheckBoxField
                          name={`coverage-${coverage.uuid}`}
                          label={coverage.name}
                          checked={!!selectedCoverages[coverage.uuid]}
                          handleChange={handleCoverageChange(coverage)}
                          labelPlacement="end"
                          customFont="14px"
                          customWeight="500"
                          data-testid="claim-overview-coverage-checkbox"
                        />
                      </Box>
                      {coverage.subCoverages.map((subcoverage) => (
                        <Box
                          key={subcoverage.uuid}
                          sx={{
                            ml: 2,
                            mb: 0,
                            display: 'flex',
                            alignItems: 'flex-start',
                            '& .MuiFormControlLabel-root': {
                              alignItems: 'flex-start',
                            },
                            '& .MuiButtonBase-root': {
                              paddingTop: '1px',
                            },
                          }}
                        >
                          <FormCheckBoxField
                            name={`subcoverage-${subcoverage.uuid}`}
                            data-testid="claim-overview-subCoverage-checkbox"
                            label={subcoverage.name}
                            checked={!!selectedSubCoverages[subcoverage.uuid]}
                            handleChange={handleSubCoverageChange(
                              subcoverage.uuid,
                              coverage.uuid
                            )}
                            disabled={!selectedCoverages[coverage.uuid]}
                            labelPlacement="end"
                            customFont="14px"
                            customWeight="500"
                          />
                        </Box>
                      ))}
                    </Box>
                  ))}
              </Box>

              {/* First Party column */}
              <Box sx={{ width: '25%' }}>
                <Box
                  className="coverage-title"
                  sx={{
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  First-Party Coverage
                </Box>
                {coverageData
                  .filter((coverage) => coverage.type === 'first_party')
                  .map((coverage) => (
                    <Box
                      key={coverage.uuid}
                      sx={{
                        mb: coverage.subCoverages.length > 0 ? 0.7 : 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: coverage.subCoverages.length > 0 ? 0.2 : 1,
                          '& .MuiFormControlLabel-root': {
                            alignItems: 'flex-start',
                          },
                          '& .MuiButtonBase-root': {
                            paddingTop: '1px',
                          },
                        }}
                      >
                        <FormCheckBoxField
                          name={`coverage-${coverage.uuid}`}
                          label={coverage.name}
                          checked={!!selectedCoverages[coverage.uuid]}
                          handleChange={handleCoverageChange(coverage)}
                          labelPlacement="end"
                          customFont="14px"
                          customWeight="500"
                          data-testid="claim-overview-coverage-checkbox"
                        />
                      </Box>
                      {coverage.subCoverages.map((subcoverage) => (
                        <Box
                          key={subcoverage.uuid}
                          sx={{
                            ml: 2,
                            mb: 0,
                            display: 'flex',
                            alignItems: 'flex-start',
                            '& .MuiFormControlLabel-root': {
                              alignItems: 'flex-start',
                            },
                            '& .MuiButtonBase-root': {
                              paddingTop: '1px',
                            },
                          }}
                        >
                          <FormCheckBoxField
                            name={`subcoverage-${subcoverage.uuid}`}
                            label={subcoverage.name}
                            checked={!!selectedSubCoverages[subcoverage.uuid]}
                            handleChange={handleSubCoverageChange(
                              subcoverage.uuid,
                              coverage.uuid
                            )}
                            disabled={!selectedCoverages[coverage.uuid]}
                            labelPlacement="end"
                            customFont="14px"
                            customWeight="500"
                            data-testid="claim-overview-subCoverage-checkbox"
                          />
                        </Box>
                      ))}
                    </Box>
                  ))}
              </Box>

              {/* First Party Loss column */}
              <Box sx={{ width: '25%' }}>
                <Box
                  className="coverage-title"
                  sx={{
                    maxWidth: '260px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  First-Party Loss of Business Income Coverages
                </Box>
                {coverageData
                  ?.filter(
                    (coverage: Coverage) => coverage.type === 'first_party_loss'
                  )
                  .map((coverage: Coverage) => (
                    <Box
                      key={coverage.uuid}
                      sx={{
                        mb: coverage.subCoverages.length > 0 ? 0.7 : 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: coverage.subCoverages.length > 0 ? 0.2 : 1,
                          '& .MuiFormControlLabel-root': {
                            alignItems: 'flex-start',
                          },
                          '& .MuiButtonBase-root': {
                            paddingTop: '1px',
                          },
                        }}
                      >
                        <FormCheckBoxField
                          name={`coverage-${coverage.uuid}`}
                          label={coverage.name}
                          checked={!!selectedCoverages[coverage.uuid]}
                          handleChange={handleCoverageChange(coverage)}
                          labelPlacement="end"
                          customFont="14px"
                          customWeight="500"
                          data-testid="claim-overview-coverage-checkbox"
                        />
                      </Box>
                      {coverage.subCoverages.map((subcoverage: SubCoverage) => (
                        <Box
                          key={subcoverage.uuid}
                          sx={{
                            ml: 2,
                            mb: 0,
                            display: 'flex',
                            alignItems: 'center',
                            '& .MuiFormControlLabel-root': {
                              alignItems: 'flex-start',
                            },
                            '& .MuiButtonBase-root': {
                              paddingTop: '1px',
                            },
                          }}
                        >
                          <FormCheckBoxField
                            name={`subcoverage-${subcoverage.uuid}`}
                            label={subcoverage.name}
                            checked={!!selectedSubCoverages[subcoverage.uuid]}
                            handleChange={handleSubCoverageChange(
                              subcoverage.uuid,
                              coverage.uuid
                            )}
                            disabled={!selectedCoverages[coverage.uuid]}
                            labelPlacement="end"
                            customFont="14px"
                            customWeight="500"
                            data-testid="claim-overview-subCoverage-checkbox"
                          />
                        </Box>
                      ))}
                    </Box>
                  ))}
              </Box>
              <Box sx={{ width: '20%' }}>
                <Box
                  className="coverage-title"
                  sx={{
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  ENDORSEMENTS
                </Box>
                {endormsnetData.map((endorsement: Endorsment) => (
                  <Box key={endorsement.uuid}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 1,
                        '& .MuiFormControlLabel-root': {
                          alignItems: 'flex-start',
                        },
                        '& .MuiButtonBase-root': {
                          paddingTop: '1px',
                        },
                      }}
                    >
                      <FormCheckBoxField
                        name={`${endorsement.uuid}`}
                        label={endorsement.name}
                        checked={!!selectedEndorsement[endorsement.uuid]}
                        handleChange={handleEndorsmentChange(endorsement)}
                        labelPlacement="end"
                        customFont="14px"
                        customWeight="500"
                        data-testid="claim-overview-coverage-checkbox"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <CircularLoader className="circular-loader-style" />
          )}
        </ConvergeDetailAccordion>

        <ConvergeDetailAccordion
          expanded={isAddressOpen}
          onChange={handleAddressAccordion}
          title="Additional Coverages Impacted"
          description="Please review all endorsement documents to see any additional coverages for this insured. Add all that may be impacted."
          testId="additional-coverages-accordion"
        >
          <Box
            sx={{
              display: 'flex',

              width: '40%',
              gap: 2,
              paddingLeft: '25px',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: 'row',
                paddingLeft: '2px',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <NewFormTextField
                  name="newCoverageName"
                  autoComplete={false}
                  placeholderText="Enter Coverage Name"
                  data-testid="claim-overview-new-coverage-name"
                  value={additionalNewCoverage}
                  onChange={(e) => setAdditionalNewCoverage(e.target.value)}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      padding: '8px',
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '10px',
                    },
                  }}
                />
              </Box>
              <Box className="add-coverage-container">
                <NewConvergeButton
                  variant="outlined"
                  label={'ADD COVERAGE'}
                  onClick={() => {
                    if (additionalNewCoverage.trim()) {
                      const newCoverage = {
                        name: additionalNewCoverage.trim(),
                        uuid: `additionalCoverage-${Date.now()}`,
                      };
                      setNewCoverageName([...newCoverageName, newCoverage]);
                      setAdditionalNewCoverage('');
                      addingNewCoverageHandler(
                        [...newCoverageName, newCoverage],
                        'added'
                      );
                    }
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                marginTop: '10px',
              }}
            >
              {newCoverageName?.map((newCoverage: AdditionalCoverage) => {
                return (
                  <Box
                    key={newCoverage.uuid}
                    className="claim-new-coverage-list"
                  >
                    <DeleteOutlineOutlinedIcon
                      onClick={() => {
                        const updatedList = newCoverageName.filter(
                          (item) => item.uuid !== newCoverage.uuid
                        );
                        setNewCoverageName(updatedList);
                        addingNewCoverageHandler(updatedList, 'deleted');
                      }}
                      sx={{
                        cursor: 'pointer',
                        color: 'black',
                      }}
                    />
                    <Box
                      sx={{ ml: 1, color: 'black' }}
                      className="claim-new-coverage-name"
                    >
                      {newCoverage?.name &&
                        capitalizeLetterRemoveUnderLine(newCoverage?.name)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </ConvergeDetailAccordion>
      </Paper>
    </Box>
  );
};
